import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate, STATUS_BADGE } from "@/lib/format";
import { printSupplierInvoice, printSupplierCombinedInvoice } from "@/lib/invoice";
import { PeriodFilter } from "@/components/PeriodFilter";
import { SupplierDebtRecap } from "@/components/SupplierDebtRecap";
import { ReassignAdminDialog } from "@/components/ReassignAdminDialog";
import { ReconcileDialog } from "@/components/ReconcileDialog";
import { useLimits, ShowMore } from "@/components/ShowMore";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingDown, CheckCircle2, Building2, Wallet, Printer, Search, UserCog, AlertTriangle, Clock, HandCoins, Pencil, Eye, History, RotateCcw, ShieldCheck,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const PERIOD_LABELS = { today: "Hari ini", week: "Minggu ini", month: "Bulan ini", year: "Tahun ini", all: "Semua periode" };

const STATUS_FILTERS = [
  { id: "all", label: "Semua Status" },
  { id: "unpaid", label: "Belum Dibayar" },
  { id: "partial", label: "Dibayar Sebagian" },
  { id: "paid", label: "Lunas" },
  { id: "soon", label: "Akan Jatuh Tempo" },
  { id: "overdue", label: "Sudah Jatuh Tempo" },
];

const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0);

function StatusBadge({ b }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge variant="outline" className={STATUS_BADGE[b.payment_status]}>{b.payment_status}</Badge>
      {b.overdue && <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300 text-[10px] px-1.5 py-0">Jatuh Tempo</Badge>}
      {!b.overdue && b.due_bucket === "soon" && <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300 text-[10px] px-1.5 py-0">Segera</Badge>}
      {b.due_bucket === "undetermined" && b.payment_status !== "Lunas" && <span className="text-[10px] text-muted-foreground">Tempo belum ditentukan</span>}
    </div>
  );
}

export default function Bills() {
  const { user } = useAuth();
  const canPay = user.role === "owner" || user.can_pay_supplier;
  const [period, setPeriod, range, setRange] = usePersistentPeriod("bills", "all");
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [opts, setOpts] = useState(null);
  const [adminFilter, setAdminFilter] = useState("all");
  const [adminList, setAdminList] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [showReassign, setShowReassign] = useState(false);
  const [showRecon, setShowRecon] = useState(false);
  const { lim, more } = useLimits(30);
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const qstr = () => (period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`);
  const periodLabel = () => (period === "custom" ? `${range.start} s/d ${range.end}` : PERIOD_LABELS[period]);
  const load = () => { api.get(`/bills?${qstr()}`).then((r) => setBills(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, range.start, range.end]);
  useEffect(() => {
    api.get("/users?role=supplier").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/meta/options").then((r) => setOpts(r.data)).catch(() => {});
    api.get("/bills/admins").then((r) => { setAdminList(r.data.admins || []); setAdminUsers(r.data.users || []); }).catch(() => {});
  }, []);

  const admins = useMemo(() => {
    const set = new Set(adminList);
    bills.forEach((b) => set.add(b.created_by_name || "Tanpa Admin"));
    return Array.from(set).sort((a, b) => (a === "Tanpa Admin") - (b === "Tanpa Admin") || a.localeCompare(b));
  }, [bills, adminList]);
  const supplierNames = useMemo(() => Array.from(new Set(bills.map((b) => b.supplier_name || "-"))), [bills]);
  const supPhone = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.name, s.phone || ""])), [suppliers]);

  const matchBy = (b, f) => {
    if (f === "all") return true;
    if (f === "unpaid") return b.payment_status === "Belum Dibayar";
    if (f === "partial") return b.payment_status === "Dibayar Sebagian";
    if (f === "paid") return b.payment_status === "Lunas";
    if (f === "overdue") return b.overdue;
    if (f === "soon") return b.due_bucket === "soon";
    return true;
  };
  const matchStatus = (b) => matchBy(b, statusFilter);

  const filtered = useMemo(() => bills.filter((b) => {
    if (adminFilter !== "all" && (b.created_by_name || "Tanpa Admin") !== adminFilter) return false;
    if (supplierFilter !== "all" && (b.supplier_name || "-") !== supplierFilter) return false;
    if (!matchStatus(b)) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!((b.invoice_no || "").toLowerCase().includes(s) || (b.supplier_name || "").toLowerCase().includes(s) || (b.description || "").toLowerCase().includes(s))) return false;
    }
    return true;
  }), [bills, adminFilter, supplierFilter, statusFilter, search]);

  const rekap = useMemo(() => ({
    count: filtered.length,
    tagihan: sum(filtered, (b) => b.amount),
    dibayar: sum(filtered, (b) => b.paid),
    hutang: sum(filtered.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining),
    overdue: sum(filtered.filter((b) => b.overdue), (b) => b.remaining),
    soon: sum(filtered.filter((b) => b.due_bucket === "soon"), (b) => b.remaining),
  }), [filtered]);

  const groups = useMemo(() => {
    const byAdmin = {};
    filtered.forEach((b) => {
      const admin = b.created_by_name || "Tanpa Admin";
      const sup = b.supplier_name || "-";
      byAdmin[admin] = byAdmin[admin] || { admin, suppliers: {} };
      byAdmin[admin].suppliers[sup] = byAdmin[admin].suppliers[sup] || { supplier: sup, bills: [] };
      byAdmin[admin].suppliers[sup].bills.push(b);
    });
    return Object.values(byAdmin).map((g) => ({
      ...g,
      suppliers: Object.values(g.suppliers),
      bills: Object.values(g.suppliers).flatMap((s) => s.bills),
    }));
  }, [filtered]);

  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", account: "Bank BCA", date: today(), ref: "", note: "" });
  const [payProof, setPayProof] = useState("");
  const [payProofBusy, setPayProofBusy] = useState(false);
  const [detailFor, setDetailFor] = useState(null);

  const uploadProof = async (file, setPath, setBusyFlag) => {
    if (!file) return;
    setBusyFlag(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/payment-proof", fd);
      setPath(r.data.path);
      toast.success("Bukti pembayaran terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah bukti"); }
    finally { setBusyFlag(false); }
  };

  const viewProof = async (path) => {
    try {
      const r = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Gagal membuka bukti pembayaran"); }
  };

  const submitPay = async () => {
    if (!payForm.amount) { toast.error("Isi jumlah pembayaran"); return; }
    if (!payProof) { toast.error("Bukti gambar pembayaran wajib disertakan"); return; }
    try {
      await api.patch(`/bills/${payFor._id}/pay`, { ...payForm, amount: parseFloat(payForm.amount), proof_image: payProof });
      toast.success("Pembayaran supplier dicatat");
      setPayFor(null); setPayForm({ amount: "", account: "Bank BCA", date: today(), ref: "", note: "" }); setPayProof("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [groupPayFor, setGroupPayFor] = useState(null);
  const [groupPayForm, setGroupPayForm] = useState({ amount: "", account: "Bank BCA", date: today(), ref: "", note: "" });
  const [groupProof, setGroupProof] = useState("");
  const [groupProofBusy, setGroupProofBusy] = useState(false);
  const [groupPayBusy, setGroupPayBusy] = useState(false);
  const [payMode, setPayMode] = useState("full");
  const billsRemaining = (bs) => sum(bs.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining);
  const groupRemaining = (g) => billsRemaining(g.bills);
  const openGroupPay = (scope, bills) => {
    setGroupPayFor({ scope, bills });
    setPayMode("full");
    setGroupProof("");
    setGroupPayForm({ amount: String(billsRemaining(bills)), account: "Bank BCA", date: today(), ref: "", note: "" });
  };
  const submitGroupPay = async () => {
    const amt = parseFloat(groupPayForm.amount);
    if (!amt || amt <= 0) { toast.error("Isi nominal pembayaran"); return; }
    if (!groupProof) { toast.error("Bukti gambar pembayaran wajib disertakan"); return; }
    const unpaid = groupPayFor.bills
      .filter((b) => b.payment_status !== "Lunas" && b.remaining > 0)
      .sort((a, b2) => (a.issue_date || "").localeCompare(b2.issue_date || ""));
    const totalSisa = sum(unpaid, (b) => b.remaining);
    if (amt > totalSisa) { toast.error(`Nominal melebihi sisa hutang (${formatIDR(totalSisa)})`); return; }
    let sisa = amt;
    const pays = [];
    for (const b of unpaid) {
      if (sisa <= 0) break;
      const bayar = Math.min(b.remaining, sisa);
      pays.push({ id: b._id, bayar });
      sisa -= bayar;
    }
    setGroupPayBusy(true);
    try {
      // Nominal per tagihan sudah dihitung di atas — kirim paralel agar tidak menunggu satu per satu
      await Promise.all(pays.map((p) => api.patch(`/bills/${p.id}/pay`, { ...groupPayForm, amount: p.bayar, proof_image: groupProof })));
      toast.success(`Pembayaran ${formatIDR(amt)} dicatat ke ${pays.length} tagihan`);
      setGroupPayFor(null);
      setGroupProof("");
      setGroupPayForm({ amount: "", account: "Bank BCA", date: today(), ref: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
    finally { setGroupPayBusy(false); }
  };

  const [showPayHistory, setShowPayHistory] = useState(false);
  const [historyPayments, setHistoryPayments] = useState(null);
  const allPayments = useMemo(() => bills
    .flatMap((b) => (b.payments || []).map((p) => ({ ...p, invoice_no: b.invoice_no, supplier_name: b.supplier_name, admin: b.created_by_name })))
    .sort((a, b2) => (b2.date || "").localeCompare(a.date || "")), [bills]);
  useEffect(() => {
    if (!showPayHistory) return;
    api.get("/bills?period=all").then((r) => setHistoryPayments(
      r.data.flatMap((b) => (b.payments || []).map((p) => ({ ...p, invoice_no: b.invoice_no, supplier_name: b.supplier_name, admin: b.created_by_name })))
        .sort((a, b2) => (b2.date || "").localeCompare(a.date || ""))
    )).catch(() => setHistoryPayments(null));
  }, [showPayHistory]);
  const histRows = historyPayments || allPayments;
  const payTotals = useMemo(() => {
    const tstr = today();
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const wstr = weekStart.toISOString().slice(0, 10);
    const mstr = tstr.slice(0, 7);
    const ystr = tstr.slice(0, 4);
    const inPeriod = (d) => {
      if (period === "all") return true;
      if (period === "custom") return (!range.start || d >= range.start) && (!range.end || d <= range.end);
      const s = { today: tstr, week: wstr, month: `${mstr}-01`, year: `${ystr}-01-01` }[period];
      return s ? d >= s : true;
    };
    const t = { today: 0, week: 0, month: 0, year: 0, period: 0 };
    histRows.forEach((x) => {
      const d = (x.date || "").slice(0, 10);
      if (!d) return;
      if (d === tstr) t.today += x.amount;
      if (d >= wstr) t.week += x.amount;
      if (d.slice(0, 7) === mstr) t.month += x.amount;
      if (d.slice(0, 4) === ystr) t.year += x.amount;
      if (inPeriod(d)) t.period += x.amount;
    });
    return t;
    // eslint-disable-next-line
  }, [histRows, period, range.start, range.end]);

  const [editFor, setEditFor] = useState(null);
  const [eform, setEform] = useState(null);
  const openEdit = (b) => {
    setEditFor(b);
    setEform({ amount: String(b.amount || 0), description: b.description || "", issue_date: b.issue_date || today(), due_date: b.due_date || "" });
  };
  const submitEdit = async () => {
    if (!eform.amount) { toast.error("Nominal wajib diisi"); return; }
    try {
      await api.patch(`/bills/${editFor._id}`, {
        amount: parseFloat(eform.amount) || 0,
        description: eform.description,
        issue_date: eform.issue_date || null,
        due_date: eform.due_date || null,
      });
      toast.success("Tagihan diperbarui");
      setEditFor(null); setEform(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cards = [
    { label: "Total Hutang Supplier", value: rekap.hutang, sub: `${filtered.filter((b) => b.payment_status !== "Lunas").length} tagihan belum lunas`, icon: TrendingDown, tone: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"] },
    { label: "Sudah Dibayar", value: rekap.dibayar, sub: `dari ${formatIDR(rekap.tagihan)}`, icon: CheckCircle2, tone: ["bg-emerald-100 dark:bg-emerald-950", "text-emerald-600"] },
    { label: "Sudah Jatuh Tempo", value: rekap.overdue, sub: "sisa overdue", icon: AlertTriangle, tone: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"] },
    { label: "Akan Jatuh Tempo", value: rekap.soon, sub: "≤ 7 hari", icon: Clock, tone: ["bg-amber-100 dark:bg-amber-950", "text-amber-600"] },
  ];

  const voidPay = async (b) => {
    if (!window.confirm(`Batalkan pembayaran ${b.invoice_no} sebesar ${formatIDR(b.paid)}? Tagihan kembali "Belum Dibayar" dan saldo rekening pembayaran otomatis kembali.`)) return;
    try {
      await api.post(`/bills/${b._id}/void-payment`, { reason: "Koreksi salah input pembayaran" });
      toast.success("Pembayaran dibatalkan — tagihan kembali Belum Dibayar");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const BillRow = ({ b }) => (
    <div data-testid={`bill-row-${b.invoice_no}`} className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-3 lg:items-center px-3 py-2.5 border-b border-blue-200/70 dark:border-blue-900/40 last:border-0 text-sm bg-blue-50/70 dark:bg-blue-950/25 border-l-2 border-l-blue-400 dark:border-l-blue-500 hover:bg-blue-100/70 dark:hover:bg-blue-950/40 transition-colors">
      <div className="lg:col-span-3 min-w-0">
        <p className="font-medium flex items-center gap-1.5 flex-wrap">{b.invoice_no}
          {b.auto_generated && <span className="inline-block rounded bg-primary/10 text-primary text-[10px] font-semibold px-1.5 py-0.5">AUTO</span>}
        </p>
        {b.description && <p className="text-[11px] text-muted-foreground truncate">{b.description}</p>}
        {(b.ucapan || b.dari || b.alamat) && (
          <div className="mt-0.5 space-y-0.5 text-[11px] text-muted-foreground whitespace-pre-line break-words" data-testid={`bill-ucapan-${b.invoice_no}`}>
            {b.ucapan && <p>Ucapan: {b.ucapan}</p>}
            {b.dari && <p>Dari: {b.dari}</p>}
            {b.alamat && <p>Alamat: {b.alamat}</p>}
          </div>
        )}
        {(b.payments || []).length > 0 && (
          <p className="text-[11px] text-emerald-700 dark:text-emerald-400 truncate" data-testid={`bill-pay-note-${b.invoice_no}`}>
            Catatan bayar: {formatIDR(b.payments[b.payments.length - 1].amount)} · {formatDate(b.payments[b.payments.length - 1].date)}
            {b.payments[b.payments.length - 1].note ? ` — ${b.payments[b.payments.length - 1].note}` : ""}
          </p>
        )}
      </div>
      <div className="lg:col-span-2 text-[12px] text-muted-foreground">{b.due_date ? formatDate(b.due_date) : "Belum Ditentukan"}{typeof b.days_to_due === "number" && b.payment_status !== "Lunas" ? <span className={`block text-[10px] ${b.overdue ? "text-rose-500" : ""}`}>{b.overdue ? `telat ${Math.abs(b.days_to_due)} hari` : `${b.days_to_due} hari lagi`}</span> : null}</div>
      <div className="lg:col-span-2 lg:text-right font-semibold"><span className="lg:hidden text-muted-foreground font-normal text-[11px]">Tagihan: </span>{formatIDR(b.amount)}</div>
      <div className="lg:col-span-1 lg:text-right text-emerald-600"><span className="lg:hidden text-muted-foreground text-[11px]">Dibayar: </span>{formatIDR(b.paid)}</div>
      <div className="lg:col-span-1 lg:text-right text-rose-600"><span className="lg:hidden text-muted-foreground text-[11px]">Sisa: </span>{formatIDR(b.remaining)}</div>
      <div className="lg:col-span-2"><StatusBadge b={b} /></div>
      <div className="lg:col-span-1 flex lg:justify-end gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => setDetailFor(b)} data-testid={`bill-detail-${b.invoice_no}`} title="Detail & Riwayat"><Eye className="h-4 w-4" /></Button>
        {b.payment_status !== "Lunas" && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => printSupplierInvoice(b)} data-testid={`bill-invoice-${b.invoice_no}`} title="Cetak Invoice"><Printer className="h-4 w-4" /></Button>
        )}
        {canPay && !b.auto_generated && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(b)} data-testid={`bill-edit-${b.invoice_no}`} title="Edit Tagihan"><Pencil className="h-4 w-4" /></Button>
        )}
        {canPay && b.remaining > 0 && (
          <Button variant="outline" size="sm" className="h-8 gap-1 px-2" onClick={() => { setPayProof(""); setPayFor(b); setPayForm({ amount: String(b.remaining), account: "Bank BCA", date: today(), ref: "", note: "" }); }} data-testid={`bill-pay-${b.invoice_no}`}>
            <Wallet className="h-3.5 w-3.5" /> Bayar
          </Button>
        )}
        {canPay && (b.paid || 0) > 0 && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600" onClick={() => voidPay(b)} data-testid={`bill-void-${b.invoice_no}`} title="Batalkan Pembayaran (salah input)"><RotateCcw className="h-4 w-4" /></Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Pembayaran Supplier (HPP)</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Kewajiban ke supplier dikelompokkan per admin. Pembayaran mengurangi saldo kas/bank.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
          <Button variant="outline" className="gap-1.5 shrink-0" onClick={() => setShowPayHistory(true)} data-testid="pay-history-btn">
            <History className="h-4 w-4" /> Riwayat Pembayaran
          </Button>
          {user.role === "owner" && (
            <Button variant="outline" className="gap-1.5 shrink-0" onClick={() => setShowRecon(true)} data-testid="reconcile-btn">
              <ShieldCheck className="h-4 w-4" /> Rekonsiliasi
            </Button>
          )}
          <ReconcileDialog open={showRecon} onOpenChange={setShowRecon} onFixed={load} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="supplier-rekap">
        {cards.map((c) => (
          <Card key={c.label} data-testid={`supplier-card-${c.label}`} className="p-5">
            <div className="flex items-center gap-3">
              <div className={`grid place-items-center h-11 w-11 rounded-xl ${c.tone[0]}`}><c.icon className={`h-5 w-5 ${c.tone[1]}`} /></div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</p>
                <p className="font-heading text-sm sm:text-lg font-extrabold break-all leading-tight">{formatIDR(c.value)}</p>
                <p className="text-[10px] text-muted-foreground">{c.sub}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-2.5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="supplier-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no. invoice, supplier, keterangan..." className="pl-9" />
        </div>
        <Select value={adminFilter} onValueChange={setAdminFilter}>
          <SelectTrigger data-testid="supplier-admin-filter" className="lg:w-44"><SelectValue placeholder="Admin" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Admin</SelectItem>{admins.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger data-testid="supplier-supplier-filter" className="lg:w-44"><SelectValue placeholder="Supplier" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Supplier</SelectItem>{supplierNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="supplier-status-tabs">
        {STATUS_FILTERS.map((s) => {
          const base = bills.filter((b) =>
            (adminFilter === "all" || (b.created_by_name || "Tanpa Admin") === adminFilter) &&
            (supplierFilter === "all" || (b.supplier_name || "-") === supplierFilter));
          const rows = base.filter((b) => matchBy(b, s.id));
          const amt = rows.reduce((a, x) => a + (x.amount || 0), 0);
          const sisa = rows.reduce((a, x) => a + (x.remaining || 0), 0);
          return (
            <button key={s.id} type="button" data-testid={`supp-tab-${s.id}`} onClick={() => setStatusFilter(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === s.id ? "bg-[#0A1633] text-white border-[#0A1633]" : "bg-card text-muted-foreground border-border hover:border-[#C9A227]/60"}`}>
              {s.label} · {rows.length} · {formatIDR(amt)}{sisa > 0 && s.id !== "paid" && s.id !== "all" ? ` (sisa ${formatIDR(sisa)})` : ""}
            </button>
          );
        })}
      </div>

      <SupplierDebtRecap bills={filtered} extraAdmins={adminFilter === "all" ? adminList : [adminFilter]} periodLabel={periodLabel()}
        activeAdmin={adminFilter}
        onSelectAdmin={(a) => {
          setAdminFilter(adminFilter === a ? "all" : a);
          setTimeout(() => document.getElementById("bills-groups")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
        }}
        onReassign={user.role === "owner" ? () => setShowReassign(true) : null} />
      <ReassignAdminDialog open={showReassign} onOpenChange={setShowReassign} adminUsers={adminUsers}
        bills={bills.filter((b) => !b.created_by_name)} onDone={load} />
      <div id="bills-groups" className="scroll-mt-20" />

      {groups.length === 0 && <Card data-testid="bills-empty-state" className="p-10 text-center text-muted-foreground text-sm">{adminFilter !== "all" ? `Belum ada tagihan supplier untuk ${adminFilter} — total hutang Rp0.` : "Tidak ada tagihan pada filter ini."}</Card>}

      {groups.map((g) => {
        const gBills = g.bills;
        return (
          <Card key={g.admin} data-testid={`admin-group-${g.admin}`} className="overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-[#0A1633] text-white">
              <div className="flex items-center gap-2 min-w-0">
                <div className="grid place-items-center h-8 w-8 rounded-lg bg-white/10 border border-[#C9A227]/40 shrink-0"><UserCog className="h-4 w-4 text-[#C9A227]" /></div>
                <div className="min-w-0"><p className="font-heading font-semibold text-sm truncate">{g.admin}</p><p className="text-[11px] text-blue-100/70">{gBills.length} order · {g.suppliers.length} supplier</p></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-blue-100/70">Sisa Hutang</p>
                  <p className="font-heading font-bold text-sm">{formatIDR(sum(gBills.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining))}</p>
                </div>
                {canPay && groupRemaining(g) > 0 && (
                  <Button size="sm" variant="gold" className="h-8 gap-1 text-xs" onClick={() => openGroupPay(g.admin, gBills)} data-testid={`pay-sisa-${g.admin}`}>
                    <Wallet className="h-3.5 w-3.5" /> Bayar
                  </Button>
                )}
              </div>
            </div>

            {g.suppliers.slice(0, lim(`s|${g.admin}`)).map((sg) => (
              <div key={sg.supplier} className="border-b border-border last:border-0">
                <div className="flex items-center justify-between gap-2 px-4 py-2 bg-blue-100/80 dark:bg-blue-950/50 border-y border-blue-200/70 dark:border-blue-900/40">
                  <div className="flex items-center gap-2 min-w-0"><Building2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0" /><span className="text-xs font-semibold truncate text-blue-900 dark:text-blue-100">{sg.supplier}</span><span className="text-[10px] text-blue-500 dark:text-blue-400">({sg.bills.length})</span></div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap hidden md:inline">Tagihan <b className="text-foreground">{formatIDR(sum(sg.bills, (b) => b.amount))}</b></span>
                    <span className="text-[11px] text-emerald-600 whitespace-nowrap hidden md:inline">Bayar {formatIDR(sum(sg.bills, (b) => b.paid))}</span>
                    <span className="text-[11px] text-rose-600 whitespace-nowrap hidden md:inline">Sisa {formatIDR(sum(sg.bills.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining))}</span>
                    {canPay && billsRemaining(sg.bills) > 0 && (
                      <Button size="sm" variant="gold" className="h-7 text-xs gap-1 px-2" onClick={() => openGroupPay(`${g.admin} · ${sg.supplier}`, sg.bills)} data-testid={`pay-supp-${g.admin}-${sg.supplier}`}>
                        <Wallet className="h-3 w-3" /> Bayar
                      </Button>
                    )}
                    {sg.bills.some((b) => b.payment_status !== "Lunas") && (
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => printSupplierCombinedInvoice({ admin: g.admin, supplier: sg.supplier, phone: supPhone[sg.supplier] || "", periodLabel: periodLabel(), bills: sg.bills.filter((b) => b.payment_status !== "Lunas") })} data-testid={`print-supp-${g.admin}-${sg.supplier}`}>
                        <Printer className="h-3 w-3" /> Cetak Invoice Supplier
                      </Button>
                    )}
                  </div>
                </div>
                <div className="hidden lg:grid grid-cols-12 gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-300 bg-blue-100/50 dark:bg-blue-950/30 border-b border-blue-200/70 dark:border-blue-900/40">
                  <div className="col-span-3">Invoice</div><div className="col-span-2">Jatuh Tempo</div><div className="col-span-2 text-right">Tagihan</div><div className="col-span-1 text-right">Bayar</div><div className="col-span-1 text-right">Sisa</div><div className="col-span-2">Status</div><div className="col-span-1 text-right">Aksi</div>
                </div>
                {sg.bills.slice(0, lim(`b|${g.admin}|${sg.supplier}`)).map((b) => <BillRow key={b._id} b={b} />)}
                <ShowMore shown={Math.min(lim(`b|${g.admin}|${sg.supplier}`), sg.bills.length)} total={sg.bills.length} label="tagihan"
                  onMore={() => more(`b|${g.admin}|${sg.supplier}`)} testid={`more-bills-${g.admin}-${sg.supplier}`} />
              </div>
            ))}
            <ShowMore shown={Math.min(lim(`s|${g.admin}`), g.suppliers.length)} total={g.suppliers.length} label="supplier"
              onMore={() => more(`s|${g.admin}`)} testid={`more-suppliers-${g.admin}`} />

            <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-1 px-4 py-2.5 bg-accent/40 text-[12px]">
              <span className="text-muted-foreground">Total Order <b className="text-foreground">{gBills.length}</b></span>
              <span className="text-muted-foreground">Total Tagihan <b className="text-foreground">{formatIDR(sum(gBills, (b) => b.amount))}</b></span>
              <span className="text-emerald-600">Dibayar {formatIDR(sum(gBills, (b) => b.paid))}</span>
              <span className="text-rose-600">Sisa {formatIDR(sum(gBills.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining))}</span>
              <span className="text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{gBills.filter((b) => b.overdue).length} jatuh tempo</span>
              <span className="text-amber-600 flex items-center gap-1"><Clock className="h-3 w-3" />{gBills.filter((b) => b.due_bucket === "soon").length} akan jatuh tempo</span>
            </div>
          </Card>
        );
      })}

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><HandCoins className="h-5 w-5 text-[#C9A227]" /> Bayar Tagihan Supplier</DialogTitle>
            <DialogDescription className="text-xs">Pembayaran mengurangi saldo kas/bank yang dipilih.</DialogDescription>
          </DialogHeader>
          {payFor && (
            <div className="space-y-3 py-1">
              <div className="rounded-lg border border-border p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Supplier</span><span className="font-medium">{payFor.supplier_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">No. Invoice</span><span className="font-medium">{payFor.invoice_no}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Tagihan</span><span>{formatIDR(payFor.amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Sisa Tagihan</span><b className="text-rose-600">{formatIDR(payFor.remaining)}</b></div>
              </div>
              <div className="space-y-1.5"><Label>Nominal Pembayaran (Rp)</Label><Input data-testid="bill-pay-amount" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Metode / Rekening Sumber Dana</Label>
                <Select value={payForm.account} onValueChange={(v) => setPayForm({ ...payForm, account: v })}>
                  <SelectTrigger data-testid="bill-pay-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tanggal</Label><Input data-testid="bill-pay-date" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>No. Referensi</Label><Input data-testid="bill-pay-ref" value={payForm.ref} onChange={(e) => setPayForm({ ...payForm, ref: e.target.value })} placeholder="opsional" /></div>
              </div>
              <div className="space-y-1.5"><Label>Keterangan</Label><Input data-testid="bill-pay-note" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} placeholder="opsional" /></div>
              <div className="space-y-1.5">
                <Label>Bukti Gambar Pembayaran <span className="text-rose-600">(wajib)</span></Label>
                <Input data-testid="bill-pay-proof" type="file" accept="image/*" className="text-xs" onChange={(e) => uploadProof(e.target.files?.[0], setPayProof, setPayProofBusy)} />
                {payProofBusy && <p className="text-[11px] text-muted-foreground">Mengunggah bukti...</p>}
                {payProof && !payProofBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="bill-pay-proof-ok">Bukti terlampir ✓</p>}
                {!payProof && !payProofBusy && <p className="text-[11px] text-rose-600">Tanpa bukti gambar, pembayaran tidak bisa disimpan.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Batal</Button>
            <Button onClick={submitPay} disabled={!payProof || payProofBusy} data-testid="bill-pay-submit" variant="gold">Simpan Pembayaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPayHistory} onOpenChange={setShowPayHistory}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><History className="h-5 w-5 text-[#C9A227]" /> Riwayat Pembayaran Supplier</DialogTitle>
            <DialogDescription className="text-xs">Semua transaksi pembayaran ke supplier, terbaru paling atas.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 py-1" data-testid="pay-history-totals">
            {[
              { label: "Hari Ini", value: payTotals.today },
              { label: "Minggu Ini", value: payTotals.week },
              { label: "Bulan Ini", value: payTotals.month },
              { label: "Tahun Ini", value: payTotals.year },
              { label: `Periode (${periodLabel()})`, value: payTotals.period },
            ].map((t) => (
              <div key={t.label} className="rounded-lg border border-border px-2 py-1.5 text-center min-w-0">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground leading-tight">{t.label}</p>
                <p className="font-heading text-xs font-bold text-emerald-600 break-all leading-snug">{formatIDR(t.value)}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 py-1" data-testid="pay-history-panel">
            {histRows.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Belum ada pembayaran supplier tercatat.</p>}
            {histRows.map((p, i) => (
              <div key={i} className="rounded-lg border border-border px-3 py-2 text-xs space-y-1" data-testid={`pay-history-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm">{formatIDR(p.amount)}</span>
                  <span className="text-muted-foreground">{formatDate(p.date)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-muted-foreground">
                  <span className="truncate">{p.supplier_name || "-"} · {p.invoice_no}</span>
                  <span className="shrink-0">{p.method || p.account || "-"}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-muted-foreground">
                  <span>Admin tagihan: {p.admin || "-"}</span>
                  <span>{p.by_name ? `dibayar oleh ${p.by_name}` : ""}</span>
                </div>
                {(p.ref || p.note) && (
                  <p className="text-[11px] text-muted-foreground italic">{p.ref ? `Ref ${p.ref}` : ""}{p.ref && p.note ? " · " : ""}{p.note || ""}</p>
                )}
                {p.proof_image && (
                  <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" data-testid={`pay-history-proof-${i}`} onClick={() => viewProof(p.proof_image)}>
                    <Eye className="h-3 w-3" /> Lihat Bukti Pembayaran
                  </Button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayHistory(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!groupPayFor} onOpenChange={(o) => !o && setGroupPayFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><HandCoins className="h-5 w-5 text-[#C9A227]" /> Bayar Sisa Hutang</DialogTitle>
            <DialogDescription className="text-xs">Nominal dibagikan otomatis ke tagihan terlama lebih dulu — bisa dibayar sebagian.</DialogDescription>
          </DialogHeader>
          {groupPayFor && (
            <div className="space-y-3 py-1">
              <div className="rounded-lg border border-border p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Kelompok</span><span className="font-medium text-right">{groupPayFor.scope}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tagihan Belum Lunas</span><span>{groupPayFor.bills.filter((b) => b.payment_status !== "Lunas").length} tagihan</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Sisa Hutang</span><b className="text-rose-600">{formatIDR(billsRemaining(groupPayFor.bills))}</b></div>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={payMode === "full" ? "gold" : "outline"} className="h-7 text-xs flex-1" data-testid="group-pay-full" onClick={() => { setPayMode("full"); setGroupPayForm({ ...groupPayForm, amount: String(billsRemaining(groupPayFor.bills)) }); }}>Bayar Penuh</Button>
                <Button type="button" size="sm" variant={payMode === "partial" ? "gold" : "outline"} className="h-7 text-xs flex-1" data-testid="group-pay-partial" onClick={() => { setPayMode("partial"); setGroupPayForm({ ...groupPayForm, amount: "" }); }}>Bayar Sebagian</Button>
              </div>
              <div className="space-y-1.5"><Label>Nominal Pembayaran (Rp)</Label><Input data-testid="group-pay-amount" type="number" value={groupPayForm.amount} onChange={(e) => { setPayMode("partial"); setGroupPayForm({ ...groupPayForm, amount: e.target.value }); }} disabled={payMode === "full"} /></div>
              <div className="space-y-1.5">
                <Label>Metode / Rekening Sumber Dana</Label>
                <Select value={groupPayForm.account} onValueChange={(v) => setGroupPayForm({ ...groupPayForm, account: v })}>
                  <SelectTrigger data-testid="group-pay-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tanggal</Label><Input data-testid="group-pay-date" type="date" value={groupPayForm.date} onChange={(e) => setGroupPayForm({ ...groupPayForm, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>No. Referensi</Label><Input data-testid="group-pay-ref" value={groupPayForm.ref} onChange={(e) => setGroupPayForm({ ...groupPayForm, ref: e.target.value })} placeholder="opsional" /></div>
              </div>
              <div className="space-y-1.5"><Label>Keterangan</Label><Input data-testid="group-pay-note" value={groupPayForm.note} onChange={(e) => setGroupPayForm({ ...groupPayForm, note: e.target.value })} placeholder="opsional" /></div>
              <div className="space-y-1.5">
                <Label>Bukti Gambar Pembayaran <span className="text-rose-600">(wajib)</span></Label>
                <Input data-testid="group-pay-proof" type="file" accept="image/*" className="text-xs" onChange={(e) => uploadProof(e.target.files?.[0], setGroupProof, setGroupProofBusy)} />
                {groupProofBusy && <p className="text-[11px] text-muted-foreground">Mengunggah bukti...</p>}
                {groupProof && !groupProofBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="group-pay-proof-ok">Bukti terlampir ✓</p>}
                {!groupProof && !groupProofBusy && <p className="text-[11px] text-rose-600">Tanpa bukti gambar, pembayaran tidak bisa disimpan.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupPayFor(null)}>Batal</Button>
            <Button onClick={submitGroupPay} disabled={!groupProof || groupProofBusy || groupPayBusy} data-testid="group-pay-submit" variant="gold">{groupPayBusy ? "Menyimpan..." : "Simpan Pembayaran"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Detail Tagihan {detailFor?.invoice_no}</DialogTitle>
            <DialogDescription className="text-xs">Rincian tagihan supplier dan riwayat pembayaran.</DialogDescription>
          </DialogHeader>
          {detailFor && (
            <div className="space-y-3 py-1 text-sm" data-testid="supplier-bill-detail-panel">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <div><p className="text-[11px] text-muted-foreground">Supplier</p><p className="font-medium">{detailFor.supplier_name || "-"}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Admin</p><p className="font-medium">{detailFor.created_by_name || "-"}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Tgl Terbit</p><p className="font-medium">{formatDate(detailFor.issue_date)}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Jatuh Tempo</p><p className={`font-medium ${detailFor.overdue ? "text-rose-600" : ""}`}>{detailFor.due_date ? formatDate(detailFor.due_date) : "Belum Ditentukan"}</p></div>
                {detailFor.description && <div className="col-span-2"><p className="text-[11px] text-muted-foreground">Keterangan</p><p className="font-medium">{detailFor.description}</p></div>}
              </div>
              <div className="flex items-center gap-2"><StatusBadge b={detailFor} /></div>
              <div className="rounded-lg border border-border p-3 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Tagihan</span><span className="font-semibold">{formatIDR(detailFor.amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Dibayar</span><span className="text-emerald-600">{formatIDR(detailFor.paid)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="font-semibold">Sisa Tagihan</span><span className="font-semibold text-rose-600">{formatIDR(detailFor.remaining)}</span></div>
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">Riwayat Pembayaran</p>
                <div className="space-y-1" data-testid="supplier-bill-pay-history">
                  {(detailFor.payments || []).map((p, i) => (
                    <div key={i} className="rounded-md bg-accent/40 px-2.5 py-1.5 text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="font-medium">{formatIDR(p.amount)}</span>
                        <span className="text-muted-foreground">{formatDate(p.date)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>{p.method || p.account || "-"}{p.ref ? ` · Ref ${p.ref}` : ""}</span>
                        <span>{p.by_name ? `oleh ${p.by_name}` : ""}</span>
                      </div>
                      {p.note && <p className="text-[10px] text-muted-foreground italic">{p.note}</p>}
                      {p.proof_image && (
                        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2 mt-0.5" data-testid={`proof-link-${i}`} onClick={() => viewProof(p.proof_image)}>
                          <Eye className="h-3 w-3" /> Lihat Bukti Pembayaran
                        </Button>
                      )}
                    </div>
                  ))}
                  {(detailFor.payments || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">Belum ada pembayaran tercatat.</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {detailFor && detailFor.payment_status !== "Lunas" && (
              <Button variant="outline" onClick={() => printSupplierInvoice(detailFor)} data-testid="supplier-bill-detail-print"><Printer className="h-4 w-4 mr-1" /> Cetak Invoice</Button>
            )}
            {detailFor && canPay && detailFor.remaining > 0 && (
              <Button variant="gold" onClick={() => { setPayProof(""); setPayFor(detailFor); setPayForm({ amount: String(detailFor.remaining), account: "Bank BCA", date: today(), ref: "", note: "" }); setDetailFor(null); }} data-testid="supplier-bill-detail-pay"><Wallet className="h-4 w-4 mr-1" /> Bayar</Button>
            )}
            <Button variant="outline" onClick={() => setDetailFor(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Tagihan {editFor?.invoice_no}</DialogTitle>
            <DialogDescription className="text-xs">Hanya tagihan manual. Status pembayaran tetap dihitung otomatis.</DialogDescription>
          </DialogHeader>
          {editFor && eform && (
            <div className="space-y-3 py-1">
              <div className="space-y-1.5"><Label>Nominal Tagihan (Rp)</Label><Input data-testid="bill-edit-amount" type="number" value={eform.amount} onChange={(e) => setEform({ ...eform, amount: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Keterangan</Label><Input data-testid="bill-edit-desc" value={eform.description} onChange={(e) => setEform({ ...eform, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tgl Terbit</Label><Input data-testid="bill-edit-issue" type="date" value={eform.issue_date} onChange={(e) => setEform({ ...eform, issue_date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Jatuh Tempo</Label><Input data-testid="bill-edit-due" type="date" value={eform.due_date} onChange={(e) => setEform({ ...eform, due_date: e.target.value })} /></div>
              </div>
              {(editFor.paid || 0) > 0 && (
                <p className="text-[11px] text-amber-600">Sudah dibayar {formatIDR(editFor.paid)} — nominal baru tidak boleh lebih kecil dari jumlah ini.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={submitEdit} data-testid="bill-edit-submit" variant="gold">Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
